import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { generateHybridAnalysisContext } from '../services/AnalyticsEngine';

/**
 * ReportGenerator.js
 * Generates the Hybrid AI Analysis Report for Students
 * Updated with modern styling and dynamic text-coloring for status cells.
 */
export const downloadStudentAnalysisReport = async (paper, attempts, studentData, aiTutorInsights = null) => {
    // 1. Prepare the Hybrid Context 
    const { 
        topicAnalysis: reportData, 
        globalStats, 
        skillMetrics 
    } = generateHybridAnalysisContext(paper, attempts);
    
    const doc = new jsPDF();
    
    // Brand Colors
    const instilPink = [217, 59, 138];
    const instilPurple = [139, 92, 246];
    const textDark = [31, 41, 55];
    const textGray = [107, 114, 128];

    // Status Colors
    const colorCritical = [220, 38, 38];    // Red
    const colorStrengthening = [217, 119, 6]; // Amber
    const colorProficient = [5, 150, 105];    // Green

    // Format date strictly to DD/MM/YYYY
    const today = new Date();
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;

    // --- PAGE 1: HEADER & SCORE SUMMARY ---
    // Top Accent Border
    doc.setFillColor(instilPurple[0], instilPurple[1], instilPurple[2]);
    doc.rect(0, 0, 210, 4, 'F');

    // Title Branding
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(instilPink[0], instilPink[1], instilPink[2]);
    doc.text("Performance Analysis", 20, 22);

    // Academic Details
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(textDark[0], textDark[1], textDark[2]);
    
    doc.text(`Student:`, 20, 35);
    doc.setFont("helvetica", "bold");
    doc.text(`${studentData.name || "Student"}`, 40, 35);
    
    doc.setFont("helvetica", "normal");
    doc.text(`Paper:`, 20, 42);
    doc.setFont("helvetica", "bold");
    doc.text(`${paper.name} (${paper.level?.toUpperCase() || 'ACCA'} Level)`, 35, 42);
    
    doc.setFont("helvetica", "normal");
    doc.text(`Date:`, 20, 49);
    doc.text(`${formattedDate}`, 35, 49);

    // Score Statistics Summary
    const percentage = globalStats?.accuracy || 0;

    // Stylish Info Box for Overall Score
    doc.setFillColor(249, 250, 251);
    doc.setDrawColor(229, 231, 235);
    doc.rect(20, 56, 170, 22, 'FD'); // Fill and draw border

    doc.setFontSize(12);
    doc.setTextColor(textGray[0], textGray[1], textGray[2]);
    doc.text("Overall Syllabus Mastery", 25, 65);
    
    doc.setFontSize(18);
    doc.setTextColor(instilPurple[0], instilPurple[1], instilPurple[2]);
    doc.setFont("helvetica", "bold");
    doc.text(`${percentage}%`, 25, 74);

    doc.setFontSize(10);
    doc.setTextColor(textDark[0], textDark[1], textDark[2]);
    doc.setFont("helvetica", "normal");
    doc.text(`Score: ${globalStats?.totalScoredMarks || 0} / ${globalStats?.totalPossibleMarks || 0} Marks Total`, 130, 69);

    // --- SECTION 2: DYNAMIC SYLLABUS PERFORMANCE TABLE ---
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(instilPurple[0], instilPurple[1], instilPurple[2]);
    doc.text("Syllabus Area Weightage Analysis", 20, 92);

    const tableBody = reportData.map(data => {
        const accuracy = data.marksAllocated > 0 
            ? ((data.marksScored / data.marksAllocated) * 100).toFixed(0) 
            : 0;

        // Removed emojis to prevent jsPDF garbled text
        const statusText = accuracy < 50 ? "Critical Review" : accuracy < 75 ? "Strengthening" : "Proficient";

        return [
            data.topic,
            data.skillType.toUpperCase(),
            `${data.marksAllocated} Marks`, 
            `${accuracy}%`,
            statusText
        ];
    });

    autoTable(doc, {
        startY: 96,
        head: [['Syllabus Topic', 'Skill Focus', 'Weightage', 'Accuracy', 'Status']],
        body: tableBody,
        theme: 'grid',
        headStyles: { 
            fillColor: instilPurple, 
            fontSize: 10,
            fontStyle: 'bold',
            halign: 'left'
        },
        styles: { 
            fontSize: 9, 
            textColor: textDark,
            cellPadding: 4
        },
        alternateRowStyles: {
            fillColor: [249, 250, 251]
        },
        columnStyles: { 
            2: { halign: 'center' },
            3: { halign: 'center', fontStyle: 'bold' },
            4: { halign: 'center' } 
        },
        // Hook to dynamically color the status column text
        didParseCell: function (data) {
            if (data.section === 'body' && data.column.index === 4) {
                const status = data.cell.raw;
                data.cell.styles.fontStyle = 'bold';
                
                if (status === "Critical Review") {
                    data.cell.styles.textColor = colorCritical;
                } else if (status === "Strengthening") {
                    data.cell.styles.textColor = colorStrengthening;
                } else if (status === "Proficient") {
                    data.cell.styles.textColor = colorProficient;
                }
            }
        }
    });

    // --- SECTION 3: THE HYBRID IMPROVEMENT ROADMAP ---
    let currentY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : 120;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(instilPurple[0], instilPurple[1], instilPurple[2]);
    doc.text("Improvement Roadmap", 20, currentY);
    currentY += 10;

    reportData.forEach((topic) => {
        if (topic.marksAllocated - topic.marksScored <= 0) return;

        if (currentY > 240) {
            doc.addPage();
            currentY = 20;
        }

        // Topic Header
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(instilPink[0], instilPink[1], instilPink[2]);
        doc.text(`Focus Area: ${topic.topic} (${topic.marksAllocated - topic.marksScored} Marks Lost)`, 20, currentY);
        currentY += 6;

        // Admin's Hard-coded Advice
        doc.setFontSize(9);
        doc.setTextColor(textDark[0], textDark[1], textDark[2]);
        doc.text("Study Plan:", 25, currentY);
        
        doc.setFont("helvetica", "normal");
        const adviceText = topic.adminAdvice || "Refer to standard study texts for this area and complete targeted practice questions.";
        const adviceLines = doc.splitTextToSize(adviceText, 160);
        doc.text(adviceLines, 25, currentY + 5);
        currentY += (adviceLines.length * 5) + 8;

        // AI Tutor Insight
        if (aiTutorInsights && aiTutorInsights[topic.topic]) {
            doc.setFillColor(245, 243, 255); // Very light purple
            doc.setDrawColor(instilPurple[0], instilPurple[1], instilPurple[2]);
            doc.setLineWidth(0.3);
            
            const aiText = aiTutorInsights[topic.topic];
            const aiLines = doc.splitTextToSize(`Insight: ${aiText}`, 155);
            
            // Draw box with a subtle left border
            doc.rect(25, currentY - 4, 160, (aiLines.length * 5) + 6, 'F');
            doc.line(25, currentY - 4, 25, currentY - 4 + ((aiLines.length * 5) + 6)); 
            
            doc.setTextColor(instilPurple[0], instilPurple[1], instilPurple[2]);
            doc.setFont("helvetica", "italic");
            doc.text(aiLines, 28, currentY + 1);
            currentY += (aiLines.length * 5) + 12;
            doc.setFont("helvetica", "normal");
        } else {
            currentY += 4;
        }
    });

    // --- FOOTER ---
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(textGray[0], textGray[1], textGray[2]);
        doc.text("Generated by Hybrid Analysis Engine", 105, 285, { align: "center" });
        doc.text("Confidential Academic Record | Academic Director Approved", 105, 290, { align: "center" });
        
        // Page numbers
        doc.text(`Page ${i} of ${pageCount}`, 190, 290, { align: "right" });
    }

    doc.save(`${studentData.name || 'Student'}_${paper.name}_Analysis.pdf`);
};